import { useCallback, useEffect, useRef, useState } from 'react';

import { debounce } from 'lodash';
import { toast } from 'react-toastify';

import mainAPI from '../utils/mainAPI';

export const useDomainTable = ({ userName, serverName = 'default', onClose = () => {} }) => {
  const [domain, setDomain] = useState('');
  const [checking, setChecking] = useState(false);
  const [purchaseLoading, setPurchaseLoading] = useState(false);
  const [cost, setCost] = useState({
    value: null,
    currency: null,
    duration: null,
  });
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [step, setStep] = useState(1);

  const [sortOrder, setSortOrder] = useState(
    {
      inwx: 'DOMAINASC',
      namecheap: 'NAME',
    }[serverName]
  );
  const [search, setSearch] = useState('');
  const [domains, setDomains] = useState([]);
  const [paging, setPaging] = useState({
    TotalItems: '0',
    CurrentPage: 1,
    PageSize: 20,
  });
  const [balance, setBalance] = useState('0');
  const [balanceCurrency, setBalanceCurrency] = useState('USD');

  const abortCtrlRef = useRef(null);

  const fetchDomains = useCallback(
    async (page = 1, perPage = 20, currentSearch = search, currentSortOrder = sortOrder) => {
      abortCtrlRef.current?.abort();
      const controller = new AbortController();
      abortCtrlRef.current = controller;

      setIsLoading(true);
      try {
        const endpoint = `/domains-store/${serverName}/all-domains`;
        const params = {
          page,
          pageSize: perPage,
          userName,
          search: currentSearch,
        };

        if (serverName === 'namecheap') {
          params.SortBy = currentSortOrder;
        } else if (serverName === 'inwx') {
          params.sort = currentSortOrder;
        }

        const res = await mainAPI.get(endpoint, {
          params,
          signal: controller.signal,
        });

        // Предполагается, что API возвращает объект вида:
        // { domains: [...], totalItems: '...', balance: '...', currency: '...' }
        const { domains, totalItems, balance, currency } = res.data;

        setDomains(domains);
        setBalance(balance || '0');
        setBalanceCurrency(currency || 'USD');
        setPaging((prev) => ({ ...prev, TotalItems: totalItems }));
      } catch (err) {
        if (err.name === 'CanceledError' || err.message === 'canceled') {
          return;
        }

        console.error(`Ошибка ${serverName} при загрузке доменов:`, err);
        setError(err.message);
        setDomains([]);
        setBalance('0');
        setPaging({ TotalItems: '0', CurrentPage: 1, PageSize: 20 });
      } finally {
        setIsLoading(false);
      }
    },
    [userName, serverName, search, sortOrder]
  );

  useEffect(() => {
    fetchDomains(paging.CurrentPage, paging.PageSize, search, sortOrder);
    return () => {
      abortCtrlRef.current?.abort();
    };
  }, [fetchDomains, paging.CurrentPage, paging.PageSize, search, sortOrder]);

  const totalItems = parseInt(paging.TotalItems, 10) || 0;
  const pageSize = parseInt(paging.PageSize, 10) || 20;
  const totalPages = Math.ceil(totalItems / pageSize);

  const handleChangePage = (event, newPage) => {
    setPaging((prev) => ({ ...prev, CurrentPage: newPage + 1 }));
  };

  const handleChangeRowsPerPage = (event) => {
    const newPageSize = parseInt(event.target.value, 10);
    setPaging((prev) => ({ ...prev, PageSize: newPageSize, CurrentPage: 1 }));
  };

  const handleSearch = useCallback(
    debounce((value) => {
      setSearch(value);
      setPaging((prev) => ({ ...prev, CurrentPage: 1 }));
    }, 500),
    []
  );

  const handleCheckDomain = async () => {
    setError('');
    setChecking(true);
    try {
      const endpoint = `/domains-store/${serverName}/check-domain`;
      const res = await mainAPI.get(endpoint, {
        params: { domain, userName },
      });
      if (res.data && res.data.available) {
        toast.success(`Домен ${serverName} доступен для регистрации.`);

        setCost({
          value: res.data.price,
          currency: res.data.currency,
          duration: res.data.period,
        });
        if (res.data?.durationType) {
          setCost((prev) => ({ ...prev, durationType: res.data.durationType }));
        }
        setStep(2);
      } else {
        toast.error(`Домен ${serverName} недоступен для регистрации.`);
        setError(`Домен ${serverName} недоступен для регистрации.`);
      }
    } catch (err) {
      console.error(err);
      toast.error(`Ошибка ${serverName} проверки домена ${err?.response?.data?.message || ''}.`);
      setError(`Ошибка ${serverName} проверки домена ${err?.response?.data?.message || ''}.`);
    }
    setChecking(false);
  };

  const handlePurchaseDomain = async () => {
    setError('');
    setPurchaseLoading(true);
    try {
      const endpoint = `/domains-store/${serverName}/purchase-domain`;

      const params = { domain, userName };

      if (serverName === 'inwx') {
        params.period = '1Y';
      } else if (serverName === 'namecheap') {
        params.years = 1;
      }

      const res = await mainAPI.post(endpoint, params);

      if (res.data && res.data.success) {
        toast.success(`Домен ${serverName} успешно куплен.`);
        onClose?.();
      } else {
        toast.error(`Ошибка ${serverName} при покупке домена ${res?.data?.message || ''}.`);
        setError(`Ошибка ${serverName} при покупке домена ${res?.data?.message || ''}.`);
      }
    } catch (err) {
      console.error(err);
      toast.error(`Ошибка ${serverName} при покупке домена.`);
      setError(`Ошибка ${serverName} при покупке домена.`);
    }
    setPurchaseLoading(false);
  };

  const refreshTable = () => {
    setStep(1);
    fetchDomains();
  };

  return {
    handleCheckDomain,
    handlePurchaseDomain,
    handleChangePage,
    handleChangeRowsPerPage,
    handleSearch,
    fetchDomains,
    refreshTable,
    setDomain,
    setStep,
    setCost,
    setChecking,
    setPurchaseLoading,
    setError,
    setSortOrder,
    sortOrder,
    domains,
    domain,
    checking,
    purchaseLoading,
    step,
    search,
    cost,
    error,
    totalPages,
    totalItems,
    isLoading,
    paging,
    balance,
    balanceCurrency,
  };
};
